'use client';

import { useT } from '@repo/i18n/components/i18n.client';
import { useGetMapHeatmapQuery } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { type HeatmapEventVisibility, type SelectedHeatmapEvent, selectExternalMapId, selectHeatmapEvent, selectHeatmapEventVisibility, selectPlayerLocationDeltas, selectTitleId, selectTitleSlug, setHeatmapEventVisibility } from '@repo/store/maps/slices/mapData';
import * as MapLibre from 'maplibre-gl';
import { useEffect, useMemo, useRef } from 'react';
import { resolveHeatmapTitleConfig } from '../../data/heatmap-config';
import { getHeatmapEventCategoryLabelSelector } from '../../data/heatmap-i18n';
import { transformMapHeatmapResponse } from '../../data/heatmap-transform';
import { useMapContext } from '../../map-context';
import { MARKER_TOOLTIP_OFFSET, SOURCE_ID } from './constants';
import { attachMarkerInteractions, clearSelectedPinSource } from './interactions';
import { addHeatmap, categoryKeys, raiseHeatmapClusterOverlayLayers, removeHeatmap, syncHeatmapVisibility } from './layers';

/**
 * MapLibre heatmap overlay for titles with analytics heatmap support.
 * Density from API bins; hotspot markers + world-unit coverage disks from clusters.
 */
export function useHeatmapLayer() {
  const {
    map
  } = useMapContext();
  const dispatch = useAppDispatch();
  const {
    t
  } = useT(['game_map']);
  const titleSlug = useAppSelector(selectTitleSlug);
  const titleId = useAppSelector(selectTitleId);
  const mapExternalId = useAppSelector(selectExternalMapId);
  const visibility = useAppSelector(selectHeatmapEventVisibility);
  const playerLocationDeltas = useAppSelector(selectPlayerLocationDeltas);
  const titleConfig = useMemo(() => resolveHeatmapTitleConfig({
    titleSlug,
    titleId,
    mapExternalId
  }), [mapExternalId, titleId, titleSlug]);
  const {
    data: heatmapResponse
  } = useGetMapHeatmapQuery({
    titleId: titleConfig?.titleId ?? 0,
    eventTypes: titleConfig ? [...titleConfig.eventTypes] : [],
    days: titleConfig?.days ?? 14
  }, {
    skip: !titleConfig
  });
  const collection = useMemo(() => {
    if (!heatmapResponse || !playerLocationDeltas) return null;
    return transformMapHeatmapResponse(heatmapResponse, playerLocationDeltas);
  }, [heatmapResponse, playerLocationDeltas]);
  const addedRef = useRef(false);
  const addingRef = useRef(false);
  /** Effect id that currently holds `addingRef`; used so stale completions don't clear a newer attempt's lock. */
  const addingByEffectIdRef = useRef<number | null>(null);
  const effectIdRef = useRef(0);
  const visibilityRef = useRef(visibility);
  visibilityRef.current = visibility;
  const titleConfigRef = useRef(titleConfig);
  titleConfigRef.current = titleConfig;
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  const getLabelRef = useRef<(eventType: string) => string | undefined>(() => undefined);
  getLabelRef.current = (eventType: string) => {
    const selector = getHeatmapEventCategoryLabelSelector(eventType);
    return selector ? t(selector) : eventType;
  };
  const popupRef = useRef<MapLibre.Popup | null>(null);
  const detachInteractionsRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!collection) return;
    const defaults: HeatmapEventVisibility = {};
    let needsUpdate = false;
    for (const eventType_0 of collection.meta.event_types) {
      if (visibility[eventType_0] === undefined) {
        // Demo default: only kills visible until the user toggles others.
        defaults[eventType_0] = eventType_0 === 'kills';
        needsUpdate = true;
      }
    }
    if (needsUpdate) {
      dispatch(setHeatmapEventVisibility(defaults));
    }
  }, [collection, dispatch, visibility]);
  useEffect(() => {
    if (!map || !collection || !titleConfig) {
      addedRef.current = false;
      addingRef.current = false;
      addingByEffectIdRef.current = null;
      detachInteractionsRef.current?.();
      detachInteractionsRef.current = null;
      if (map && !map._removed && (!collection || !titleConfig)) {
        try {
          removeHeatmap(map);
        } catch {
          // Map already torn down.
        }
      }
      return;
    }
    popupRef.current ??= new MapLibre.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: MARKER_TOOLTIP_OFFSET,
      className: 'activity-heatmap-popup'
    });
    const effectId = ++effectIdRef.current;
    let cancelled = false;
    const eventTypes = categoryKeys(collection);
    const handleSelect = (selection: SelectedHeatmapEvent) => {
      clearSelectedPinSource(map);
      dispatchRef.current(selectHeatmapEvent(selection));
    };
    const attachInteractionsIfNeeded = () => {
      if (popupRef.current && !detachInteractionsRef.current) {
        detachInteractionsRef.current = attachMarkerInteractions(map, eventTypes, popupRef.current, eventType_1 => getLabelRef.current(eventType_1), handleSelect);
      }
    };
    const tryAdd = () => {
      if (cancelled || addingRef.current || map._removed) return;
      if (!map.getStyle()?.layers) return;
      const config = titleConfigRef.current;
      if (!config) return;

      // Style swap can drop our source while refs still think we added.
      if (addedRef.current && map.getSource(SOURCE_ID)) {
        syncHeatmapVisibility(map, collection, visibilityRef.current);
        raiseHeatmapClusterOverlayLayers(map, eventTypes);
        attachInteractionsIfNeeded();
        return;
      }
      addedRef.current = false;
      addingRef.current = true;
      addingByEffectIdRef.current = effectId;
      const isCurrent = () => effectIdRef.current === effectId && !cancelled;
      void addHeatmap(map, collection, visibilityRef.current, config, isCurrent).then(added => {
        // Only release the lock if this attempt still owns it — a superseded
        // completion must not clear a replacement effect's in-progress add.
        if (addingByEffectIdRef.current === effectId) {
          addingRef.current = false;
          addingByEffectIdRef.current = null;
        }
        // A newer effect owns lifecycle — don't tear down or mark added.
        if (!isCurrent()) return;
        if (map._removed) return;
        if (!added) return;
        addedRef.current = true;
        syncHeatmapVisibility(map, collection, visibilityRef.current);
        attachInteractionsIfNeeded();
        map.off('idle', tryAdd);
      }).catch(() => {
        if (addingByEffectIdRef.current === effectId) {
          addingRef.current = false;
          addingByEffectIdRef.current = null;
        }
        // Style may be mid-swap; load/idle retries cover this.
      });
    };
    if (map.isStyleLoaded()) {
      tryAdd();
    }
    map.on('load', tryAdd);
    map.on('idle', tryAdd);
    return () => {
      cancelled = true;
      map.off('load', tryAdd);
      map.off('idle', tryAdd);
      detachInteractionsRef.current?.();
      detachInteractionsRef.current = null;
      addedRef.current = false;
      if (addingByEffectIdRef.current === effectId) {
        addingRef.current = false;
        addingByEffectIdRef.current = null;
      }

      // Defer teardown so React Strict Mode remount can claim the same layers
      // instead of remove→re-add flashing markers.
      queueMicrotask(() => {
        if (effectIdRef.current !== effectId) return;
        try {
          if (!map._removed) {
            removeHeatmap(map);
          }
        } catch {
          // Map already torn down.
        }
      });
    };
  }, [map, collection, titleConfig]);
  useEffect(() => {
    if (!map || !collection || map._removed) return;
    if (!map.getSource(SOURCE_ID)) return;
    syncHeatmapVisibility(map, collection, visibility);
  }, [map, collection, visibility]);
}